// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { AttachmentUploadTask } from '@azure/communication-react';
import axios from 'axios';
import FormData from 'form-data';
import { v4 } from 'uuid';

const MAX_ATTACHMENT_SIZE_MB = 50 * 1024 * 1024;
const UNSUPPORTED_ATTACHMENTS = ['exe', 'bat', 'dat'];

export const attachmentUploadHandler = (uploadTasks: AttachmentUploadTask[], uploadToken: string | undefined): void => {
  for (const upload of uploadTasks) {
    const fileExtension = upload.file?.name.split('.').pop() ?? '';
    if (upload.file && upload.file?.size > MAX_ATTACHMENT_SIZE_MB) {
      upload.notifyUploadFailed(`"${upload.file.name}" is too big. Select an attachment under 50MB.`);
      continue;
    }

    if (UNSUPPORTED_ATTACHMENTS.includes(fileExtension)) {
      upload.notifyUploadFailed(`Uploading ".${fileExtension}" attachments is not allowed.`);
      continue;
    }

    if (!uploadToken) {
      upload.notifyUploadFailed('Unable to upload attachment, permission denied. Please try again later.');
      continue;
    }

    const uniqueAttachmentName = `${v4()}-${upload.file?.name}`;
    const formData = new FormData();
    formData.append('file', upload.file, upload.file?.name);

    try {
      axios
        .request({
          method: 'post',
          url: `/api/uploadAttachmentToAzureBlobStore`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${uploadToken}`
          },
          onUploadProgress: (p) => {
            // Cap to 99% to prevent the UI from showing 100% before the upload is actually complete, which we
            // only know after the response from the server is received.
            upload.notifyUploadProgressChanged(Math.min(p.progress ?? 0, 0.99));
          }
        })
        .catch((error) => {
          console.error(error);
          upload.notifyUploadFailed('Unable to upload attachment. Please try again later.');
        })
        .then((res) => {
          if (res && res.status === 200 && res.data && res.data.url) {
            upload.notifyUploadProgressChanged(1);
            upload.notifyUploadCompleted(uniqueAttachmentName, res.data.url);
          } else {
            upload.notifyUploadFailed('Unable to upload attachment. Please try again later.');
          }
        });
    } catch (error) {
      console.error(error);
      upload.notifyUploadFailed('Unable to upload attachment. Please try again later.');
    }
  }
};
